import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createProduct } from "@/lib/products";
import { handleApiError } from "@/lib/errors";
import { toast } from "@/components/ui/sonner";

interface NewProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NewProductDialog = ({ open, onOpenChange }: NewProductDialogProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("mennesker");
  const [price, setPrice] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setTitle("");
    setCategory("mennesker");
    setPrice("");
    setIsActive(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || !price) return;
    setSubmitting(true);
    try {
      const id = await createProduct({
        title: title.trim(),
        category: category.trim(),
        priceOre: Math.round(Number(price) * 100),
        isActive,
      });
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produktet er opprettet. Legg til beskrivelse og bilder neste.");
      onOpenChange(false);
      reset();
      navigate(`/admin/products/${id}`);
    } catch (err) {
      handleApiError(err, "Kunne ikke opprette produktet.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nytt produkt</DialogTitle>
          <DialogDescription>
            Legg inn det grunnleggende. Beskrivelse, bilder og lager redigerer du etterpå.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="np-title">Tittel *</Label>
            <Input
              id="np-title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="f.eks. Leppepomade — naturlig pleie"
              disabled={submitting}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="np-category">Kategori</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="np-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mennesker">Til deg</SelectItem>
                  <SelectItem value="hunder">Til hunden din</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="np-price">Pris (NOK, inkl. mva) *</Label>
              <Input
                id="np-price"
                type="number"
                min={0}
                step="0.01"
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="f.eks. 199"
                disabled={submitting}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-md border p-3">
            <Switch
              id="np-active"
              checked={isActive}
              onCheckedChange={setIsActive}
              disabled={submitting}
            />
            <div>
              <Label htmlFor="np-active" className="cursor-pointer">
                Aktiv i butikken
              </Label>
              <p className="text-xs text-muted-foreground">
                Skjul fra butikken hvis du vil jobbe i fred til alt er på plass.
              </p>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Oppretter …" : "Opprett produkt"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewProductDialog;
